import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatAbsoluteDateTime, formatRelativeTime } from "./dateTime";

interface Props {
  updatedAt?: string | null;
}

const REFRESH_INTERVAL_MS = 30_000;
const JUST_NOW_THRESHOLD_MS = 30_000;

export function LastSavedIndicator({ updatedAt }: Props) {
  const { t, i18n } = useTranslation();
  const [, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!updatedAt) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setRefreshTick((currentTick) => currentTick + 1);
    }, REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [updatedAt]);

  const absoluteDateTime = useMemo(
    () => formatAbsoluteDateTime(updatedAt, i18n.language),
    [i18n.language, updatedAt],
  );

  if (!updatedAt || !absoluteDateTime) {
    return null;
  }

  const millisecondsSinceUpdate = Date.now() - new Date(updatedAt).getTime();
  const relativeTime =
    millisecondsSinceUpdate >= 0 &&
    millisecondsSinceUpdate < JUST_NOW_THRESHOLD_MS
      ? t("items.edit.justNow")
      : formatRelativeTime(updatedAt, i18n.language);

  return (
    <small className="text-muted">
      <span
        tabIndex={0}
        title={absoluteDateTime}
        aria-label={`${t("items.edit.lastSaved", { relativeTime })}. ${absoluteDateTime}`}
      >
        {t("items.edit.lastSaved", { relativeTime })}
      </span>
    </small>
  );
}
